/**
 * Affiliate referral capture (PRP-MCP-10 work item 1).
 *
 * Any page of the web app opened with `?ref=CODE` (e.g.
 * `https://web.anterotrail.com/?ref=CODE` or `/signup?ref=CODE`) stores CODE in a
 * first-party cookie `aiea_ref` for 90 days (`captureReferralFromUrl`, called once
 * at startup in `main.tsx`, before the router can redirect a signed-out visitor
 * from `/` to `/login` and drop the query). The signup form prefills its optional
 * "Referral code" field from it, and `/me/bootstrap` sends it; the backend binds
 * only a brand-new account to an active affiliate and ignores everything else.
 *
 * REF_COOKIE_DAYS must equal `cookie_days` in `app/data/affiliate_rules.json`
 * (tests/mcp/test_affiliates.py checks).
 */

export const REF_COOKIE = 'aiea_ref';
export const REF_COOKIE_DAYS = 90;
const CODE_RE = /^[A-Z0-9]{6,12}$/;

/** Upper-case, trimmed code if it is 6 to 12 letters or digits, else null. */
export function normalizeReferralCode(raw: string | null | undefined): string | null {
  const code = (raw ?? '').trim().toUpperCase();
  return CODE_RE.test(code) ? code : null;
}

/** The code in the `aiea_ref` cookie, or null. */
export function readReferralCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const hit = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${REF_COOKIE}=`));
  return hit ? normalizeReferralCode(decodeURIComponent(hit.slice(REF_COOKIE.length + 1))) : null;
}

/** Stores a valid code for REF_COOKIE_DAYS; an invalid one is ignored. */
export function setReferralCookie(raw: string | null | undefined): string | null {
  const code = normalizeReferralCode(raw);
  if (!code || typeof document === 'undefined') return null;
  const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${REF_COOKIE}=${encodeURIComponent(code)}; Max-Age=${REF_COOKIE_DAYS * 86400}; Path=/; SameSite=Lax${secure}`;
  return code;
}

/** Removes the cookie (the signup form's field was cleared). */
export function clearReferralCookie(): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${REF_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
}

/** Reads `?ref=` from the URL and stores it. Returns the stored code or null. */
export function captureReferralFromUrl(search?: string): string | null {
  const query = search ?? (typeof window !== 'undefined' ? window.location.search : '');
  const ref = new URLSearchParams(query).get('ref');
  return ref ? setReferralCookie(ref) : null;
}
